'use client';

import { useMemo, useState } from 'react';
import { CalendarRange, Percent, Plus, Puzzle, Trash2, Pencil, X, BadgePercent, Layers } from 'lucide-react';
import type { Vehicle } from '@/types';
import { currencySymbol, formatMoney } from '@/lib/currency';

export interface TariffRule {
  id: string;
  name: string;
  vehicleId: string;
  startDate: string;
  endDate: string;
  multiplier: number;
}

export interface DiscountTier {
  id: string;
  minDays: number;
  percentOff: number;
}

export interface ExtraItem {
  id: string;
  name: string;
  description: string;
  pricePerDay: number;
}

interface TariffsModuleProps {
  vehicles: Vehicle[];
  rules: TariffRule[];
  setRules: (r: TariffRule[]) => void;
  tiers: DiscountTier[];
  setTiers: (t: DiscountTier[]) => void;
  extras: ExtraItem[];
  setExtras: (e: ExtraItem[]) => void;
  currency?: string | null;
  headerQuery?: string;
}

function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

const inputCls =
  'w-full rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand focus:bg-card';
const labelCls = 'mb-1 block text-xs font-semibold text-muted';
const btnAccent =
  'inline-flex items-center gap-1.5 rounded-xl bg-ink px-3 py-2 text-xs font-semibold text-shell hover:brightness-110';
const btnAccentOrange =
  'inline-flex items-center gap-1.5 rounded-xl bg-[#FF8A00] px-3 py-2 text-xs font-semibold text-slate-950 hover:brightness-95';

function vehicleName(vehicles: Vehicle[], id: string): string {
  if (id === 'all') return 'Toute la flotte';
  return vehicles.find((v) => v.id === id)?.name ?? id;
}

export function TariffsModule({ vehicles, rules, setRules, tiers, setTiers, extras, setExtras, currency, headerQuery = '' }: TariffsModuleProps) {
  const cur = currency ?? 'MAD';
  const [showRule, setShowRule] = useState(false);
  const [ruleName, setRuleName] = useState('');
  const [ruleVehicle, setRuleVehicle] = useState('all');
  const [ruleStart, setRuleStart] = useState('');
  const [ruleEnd, setRuleEnd] = useState('');
  const [ruleMult, setRuleMult] = useState('1.2');

  const [tierDays, setTierDays] = useState('');
  const [tierPct, setTierPct] = useState('');
  const [showTier, setShowTier] = useState(false);

  const [showExtra, setShowExtra] = useState(false);
  const [editingExtra, setEditingExtra] = useState<ExtraItem | null>(null);
  const [extraName, setExtraName] = useState('');
  const [extraDesc, setExtraDesc] = useState('');
  const [extraPrice, setExtraPrice] = useState('');

  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => a.minDays - b.minDays), [tiers]);

  function resetRuleForm() {
    setRuleName('');
    setRuleVehicle('all');
    setRuleStart('');
    setRuleEnd('');
    setRuleMult('1.2');
  }

  function addRule() {
    const mult = Number(ruleMult);
    if (!ruleName.trim() || !ruleStart || !ruleEnd || !isFinite(mult) || mult <= 0) return;
    setRules([...rules, { id: uid('rule'), name: ruleName.trim(), vehicleId: ruleVehicle, startDate: ruleStart, endDate: ruleEnd, multiplier: mult }]);
    resetRuleForm();
    setShowRule(false);
  }

  function addTier() {
    const d = Number(tierDays);
    const p = Number(tierPct);
    if (!isFinite(d) || d < 1 || !isFinite(p) || p <= 0 || p >= 100) return;
    setTiers([...tiers, { id: uid('tier'), minDays: Math.floor(d), percentOff: p }]);
    setTierDays('');
    setTierPct('');
    setShowTier(false);
  }

  function openExtraModal(e?: ExtraItem) {
    if (e) {
      setEditingExtra(e);
      setExtraName(e.name);
      setExtraDesc(e.description);
      setExtraPrice(String(e.pricePerDay));
    } else {
      setEditingExtra(null);
      setExtraName('');
      setExtraDesc('');
      setExtraPrice('');
    }
    setShowExtra(true);
  }

  function saveExtra() {
    const price = Number(extraPrice);
    if (!extraName.trim() || !isFinite(price) || price < 0) return;
    if (editingExtra) {
      setExtras(extras.map((x) => (x.id === editingExtra.id ? { ...x, name: extraName.trim(), description: extraDesc.trim(), pricePerDay: price } : x)));
    } else {
      setExtras([...extras, { id: uid('extra'), name: extraName.trim(), description: extraDesc.trim(), pricePerDay: price }]);
    }
    setShowExtra(false);
    setEditingExtra(null);
  }

  return (
    <div className="space-y-6">
      {/* (a) Seasonal pricing */}
      <section className="rounded-3xl border border-line bg-card p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-ink text-shell">
              <CalendarRange className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-xs font-bold text-ink">Tarifs saisonniers</h3>
              <p className="text-xs text-muted">Multiplicateurs par période (ex. été, Ramadan, fêtes).</p>
            </div>
          </div>
          <button type="button" onClick={() => setShowRule(true)} className={btnAccentOrange}>
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-line">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="bg-ink text-left text-shell">
                <th className="px-3 py-2 font-semibold">Nom</th>
                <th className="px-3 py-2 font-semibold">S&apos;applique à</th>
                <th className="px-3 py-2 font-semibold">Période</th>
                <th className="px-3 py-2 font-semibold">Coef.</th>
                <th className="px-3 py-2 font-semibold">Exemple</th>
                <th className="px-3 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted">
                    Aucune règle saisonnière.
                  </td>
                </tr>
              )}
              {rules.filter((rule) => {
                const needle = headerQuery.trim().toLowerCase();
                return needle === '' || `${rule.name} ${rule.vehicleId}`.toLowerCase().includes(needle);
              }).map((r) => {
                const base = 300;
                const eff = Math.round(base * r.multiplier);
                return (
                  <tr key={r.id} className="border-t border-line">
                    <td className="px-3 py-2 font-semibold text-ink">{r.name}</td>
                    <td className="px-3 py-2 text-muted">{vehicleName(vehicles, r.vehicleId)}</td>
                    <td className="px-3 py-2 text-muted">
                      {r.startDate} → {r.endDate}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-lg bg-[#FF8A00]/10 px-2 py-0.5 font-bold text-[#FF8A00]">×{r.multiplier}</span>
                    </td>
                    <td className="px-3 py-2 text-muted">
                      base {formatMoney(base, cur)} × {r.multiplier} = <span className="font-bold text-ink">{formatMoney(eff, cur)}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setRules(rules.filter((x) => x.id !== r.id))}
                        className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-muted hover:border-red-300 hover:text-danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Suppr.
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* (b) Long-stay discounts */}
      <section className="rounded-3xl border border-line bg-card p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-ink text-shell">
              <BadgePercent className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-xs font-bold text-ink">Remises longue durée</h3>
              <p className="text-xs text-muted">Appliquées automatiquement selon la durée de location.</p>
            </div>
          </div>
          <button type="button" onClick={() => setShowTier(true)} className={btnAccent}>
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-line">
          <table className="w-full min-w-[480px] text-xs">
            <thead>
              <tr className="bg-ink text-left text-shell">
                <th className="px-3 py-2 font-semibold">Durée min.</th>
                <th className="px-3 py-2 font-semibold">Remise</th>
                <th className="px-3 py-2 font-semibold">Exemple</th>
                <th className="px-3 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTiers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted">
                    Aucun palier de remise.
                  </td>
                </tr>
              )}
              {sortedTiers.map((t) => (
                <tr key={t.id} className="border-t border-line">
                  <td className="px-3 py-2 font-semibold text-ink">≥ {t.minDays} jours</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 rounded-lg bg-success/10 px-2 py-0.5 font-bold text-success">
                      <Percent className="h-3 w-3" />-{t.percentOff}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted">
                    {t.minDays}j −{t.percentOff}% (ex. 7j −10%)
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setTiers(tiers.filter((x) => x.id !== t.id))}
                      className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-muted hover:border-red-300 hover:text-danger"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Suppr.
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* (c) Extras catalogue */}
      <section className="rounded-3xl border border-line bg-card p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-ink text-shell">
              <Puzzle className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-xs font-bold text-ink">Options &amp; extras</h3>
              <p className="text-xs text-muted">Siège bébé, conducteur additionnel, GPS…</p>
            </div>
          </div>
          <button type="button" onClick={() => openExtraModal()} className={btnAccent}>
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-line">
          <table className="w-full min-w-[560px] text-xs">
            <thead>
              <tr className="bg-ink text-left text-shell">
                <th className="px-3 py-2 font-semibold">Nom</th>
                <th className="px-3 py-2 font-semibold">Description</th>
                <th className="px-3 py-2 font-semibold">Prix / jour</th>
                <th className="px-3 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {extras.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted">
                    Aucun extra au catalogue.
                  </td>
                </tr>
              )}
              {extras.filter((extra) => {
                const needle = headerQuery.trim().toLowerCase();
                return needle === '' || `${extra.name} ${extra.description}`.toLowerCase().includes(needle);
              }).map((e) => (
                <tr key={e.id} className="border-t border-line">
                  <td className="px-3 py-2 font-semibold text-ink">{e.name}</td>
                  <td className="px-3 py-2 text-muted">{e.description || '—'}</td>
                  <td className="px-3 py-2 font-bold text-ink">{formatMoney(e.pricePerDay, cur)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => openExtraModal(e)}
                        className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-muted hover:border-ink"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => setExtras(extras.filter((x) => x.id !== e.id))}
                        className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-muted hover:border-red-300 hover:text-danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted">
          <Layers className="h-3.5 w-3.5" /> Utilisés à la création de réservation après connexion backend.
        </p>
      </section>

      {/* Rule modal */}
      {showRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-md rounded-3xl border border-line bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-xs font-bold text-ink">Nouvelle règle saisonnière</h4>
              <button type="button" onClick={() => setShowRule(false)} className="rounded-lg p-1 text-muted hover:bg-shell">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Nom</label>
                <input value={ruleName} onChange={(e) => setRuleName(e.target.value)} placeholder="Ex. Haute saison été" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Véhicule</label>
                <select value={ruleVehicle} onChange={(e) => setRuleVehicle(e.target.value)} className={inputCls}>
                  <option value="all">Toute la flotte</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Début</label>
                  <input type="date" value={ruleStart} onChange={(e) => setRuleStart(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Fin</label>
                  <input type="date" value={ruleEnd} onChange={(e) => setRuleEnd(e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Multiplicateur (ex. 1.2)</label>
                <input type="number" min="0.1" step="0.05" value={ruleMult} onChange={(e) => setRuleMult(e.target.value)} className={inputCls} />
                <p className="mt-1 text-xs text-muted">
                  Exemple : base 300 DH × {ruleMult || '1.2'} = {formatMoney(300 * (Number(ruleMult) || 0), cur)}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setShowRule(false)} className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-muted">
                  Annuler
                </button>
                <button type="button" onClick={addRule} className={btnAccentOrange}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tier modal */}
      {showTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-sm rounded-3xl border border-line bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-xs font-bold text-ink">Nouveau palier de remise</h4>
              <button type="button" onClick={() => setShowTier(false)} className="rounded-lg p-1 text-muted hover:bg-shell">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Jours min.</label>
                <input type="number" min="1" value={tierDays} onChange={(e) => setTierDays(e.target.value)} placeholder="7" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>% remise</label>
                <input type="number" min="1" max="99" value={tierPct} onChange={(e) => setTierPct(e.target.value)} placeholder="10" className={inputCls} />
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setShowTier(false)} className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-muted">
                Annuler
              </button>
              <button type="button" onClick={addTier} className={btnAccent}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extra modal */}
      {showExtra && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-md rounded-3xl border border-line bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-xs font-bold text-ink">{editingExtra ? 'Modifier l’extra' : 'Nouvel extra'}</h4>
              <button type="button" onClick={() => setShowExtra(false)} className="rounded-lg p-1 text-muted hover:bg-shell">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Nom</label>
                <input value={extraName} onChange={(e) => setExtraName(e.target.value)} placeholder="Ex. Siège bébé" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <input value={extraDesc} onChange={(e) => setExtraDesc(e.target.value)} placeholder="Courte description" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Prix / jour ({currencySymbol(cur)})</label>
                <input type="number" min="0" value={extraPrice} onChange={(e) => setExtraPrice(e.target.value)} className={inputCls} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setShowExtra(false)} className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-muted">
                  Annuler
                </button>
                <button type="button" onClick={saveExtra} className={btnAccent}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
